import { CommonModule } from '@angular/common';
import { Component, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import {
  LOCALE_NUMERICO,
  MAX_DECIMALES_CANTIDAD,
  MAX_DIGITOS_EXPEDIENTE
} from 'src/app/common/constants-ejb';
import { DecimalComaDirective } from 'src/app/common/directives/decimal-coma.directive';
import { DecimalHintComponent } from 'src/app/presentation/shared/decimal-hint/decimal-hint.component';
import { onlyNumbersKeypress, sanitizeNumbersOnly } from 'src/app/common/input-utils';
import { CrearTareaRequest } from 'src/app/domain/model/sin-oc/sin-oc';
import { Municipio } from 'src/app/domain/model/municipio/municipio';
import { Oficina } from 'src/app/domain/model/oficina/oficina';

@Component({
  selector: 'app-formulario-crear-tarea-sin-oc',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    DatePickerModule,
    InputNumberModule,
    TooltipModule,
    DecimalComaDirective,
    DecimalHintComponent
  ],
  template: `
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
      <div class="sm:col-span-2">
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Oficina de Atención <span class="text-red-500">*</span>
        </label>
        <p-dropdown
          [options]="oficinas()"
          optionLabel="dsoficina"
          optionValue="nmconsecutivo"
          [(ngModel)]="form().nmconsecOficina"
          placeholder="Seleccione una oficina"
          [filter]="true"
          filterBy="dsoficina"
          styleClass="w-full"
          (onChange)="oficinaChange.emit($event)"
        />
      </div>

      <div class="sm:col-span-2">
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Aseguradora <span class="text-red-500">*</span>
        </label>
        <div class="flex gap-1 min-w-0">
          <input
            pInputText
            [value]="aseguradoraLabel()"
            readonly
            placeholder="Seleccione una aseguradora..."
            class="flex-1 min-w-0 cursor-pointer"
            pTooltip="Buscar aseguradora"
            tooltipPosition="top"
            (click)="abrirBusquedaAseguradora.emit()"
          />
          <button
            pButton
            icon="pi pi-search"
            pTooltip="Buscar aseguradora"
            class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
            (click)="abrirBusquedaAseguradora.emit()"
          ></button>
          @if (form().nmconsecCliente) {
            <button
              pButton
              icon="pi pi-times"
              pTooltip="Limpiar"
              class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
              (click)="limpiarAseguradora.emit()"
            ></button>
          }
        </div>
      </div>

      <div class="sm:col-span-2">
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Beneficiario / Cliente <span class="text-red-500">*</span>
        </label>
        <div class="flex gap-1 min-w-0">
          <input
            pInputText
            [value]="nombreBeneficiarioDisplay() || ''"
            readonly
            placeholder="Seleccione un beneficiario..."
            class="flex-1 min-w-0 cursor-pointer"
            (click)="abrirBusquedaBeneficiario.emit()"
          />
          <button
            pButton
            icon="pi pi-search"
            pTooltip="Buscar beneficiario"
            class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
            (click)="abrirBusquedaBeneficiario.emit()"
          ></button>
          @if (nombreBeneficiarioDisplay()) {
            <button
              pButton
              icon="pi pi-times"
              pTooltip="Limpiar"
              class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
              (click)="limpiarBeneficiario.emit()"
            ></button>
          }
        </div>
      </div>

      <div class="sm:col-span-2">
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Tarea / Servicio <span class="text-red-500">*</span>
        </label>
        <div class="flex gap-1 min-w-0">
          <input
            pInputText
            [value]="descripcionTarea()"
            readonly
            placeholder="Seleccione una tarea..."
            class="flex-1 min-w-0 cursor-pointer"
            (click)="abrirBusquedaTarea.emit()"
          />
          <button
            pButton
            icon="pi pi-search"
            pTooltip="Buscar tarea"
            class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
            (click)="abrirBusquedaTarea.emit()"
          ></button>
          @if (form().cdservicio) {
            <button
              pButton
              icon="pi pi-times"
              pTooltip="Limpiar"
              class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
              (click)="limpiarTarea.emit()"
            ></button>
          }
        </div>
      </div>

      <div class="sm:col-span-2">
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Producto <span class="text-red-500">*</span>
        </label>
        @if (!form().cdservicio) {
          <div class="flex gap-1 min-w-0">
            <input
              pInputText
              value=""
              readonly
              disabled
              placeholder="Primero seleccione una Tarea..."
              class="flex-1 min-w-0 opacity-60 cursor-not-allowed"
              pTooltip="Debe seleccionar una Tarea antes de buscar el Producto"
              tooltipPosition="top"
            />
            <button
              pButton
              icon="pi pi-search"
              pTooltip="Debe seleccionar una Tarea primero"
              class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
              [disabled]="true"
            ></button>
          </div>
        } @else {
          <div class="flex gap-1 min-w-0">
            <input
              pInputText
              [value]="
                form().cdproducto
                  ? form().cdproducto + ' - ' + form().dsproducto
                  : ''
              "
              readonly
              placeholder="Seleccione un producto..."
              class="flex-1 min-w-0 cursor-pointer"
              (click)="abrirBusquedaProducto.emit()"
            />
            <button
              pButton
              icon="pi pi-search"
              pTooltip="Buscar producto"
              class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
              (click)="abrirBusquedaProducto.emit()"
            ></button>
            @if (form().cdproducto) {
              <button
                pButton
                icon="pi pi-times"
                pTooltip="Limpiar"
                class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                (click)="limpiarProducto.emit()"
              ></button>
            }
          </div>
        }
      </div>

      <div class="sm:col-span-2">
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Centro de Trabajo <span class="text-red-500">*</span>
        </label>
        <div class="flex gap-1 min-w-0">
          <input
            pInputText
            [value]="form().dscentroTrabajo || ''"
            readonly
            placeholder="Primero seleccione un Beneficiario..."
            class="flex-1 min-w-0"
            [class.opacity-60]="!nmconsecBeneficiarioLocal()"
            [class.cursor-not-allowed]="!nmconsecBeneficiarioLocal()"
            [class.cursor-pointer]="!!nmconsecBeneficiarioLocal()"
            pTooltip="Debe seleccionar un Beneficiario antes de buscar el Centro de Trabajo"
            tooltipPosition="top"
            [disabled]="!nmconsecBeneficiarioLocal()"
            (click)="
              nmconsecBeneficiarioLocal() && abrirBusquedaCentroTrabajo.emit()
            "
          />
          <button
            pButton
            icon="pi pi-search"
            pTooltip="Buscar centro de trabajo"
            class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
            [disabled]="!nmconsecBeneficiarioLocal()"
            (click)="abrirBusquedaCentroTrabajo.emit()"
          ></button>
          @if (form().dscentroTrabajo) {
            <button
              pButton
              icon="pi pi-times"
              pTooltip="Limpiar"
              class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
              (click)="limpiarCentroTrabajo.emit()"
            ></button>
          }
        </div>
      </div>

      <div class="sm:col-span-2">
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Consultor <span class="text-red-500">*</span>
        </label>
        <div class="flex gap-1 min-w-0">
          <input
            pInputText
            [value]="
              form().cdpersonaAsesor
                ? asesorLabel() || 'Consultor ' + form().cdpersonaAsesor
                : ''
            "
            readonly
            placeholder="Seleccione un consultor..."
            class="flex-1 min-w-0"
            [class.cursor-pointer]="!asesorBloqueado()"
            [disabled]="asesorBloqueado()"
            (click)="!asesorBloqueado() && abrirBusquedaAsesor.emit()"
          />
          @if (!asesorBloqueado()) {
            <button
              pButton
              icon="pi pi-search"
              pTooltip="Buscar consultor"
              class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
              (click)="abrirBusquedaAsesor.emit()"
            ></button>
          }
          @if (form().cdpersonaAsesor && !asesorBloqueado()) {
            <button
              pButton
              icon="pi pi-times"
              pTooltip="Limpiar"
              class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
              (click)="limpiarAsesor.emit()"
            ></button>
          }
        </div>
      </div>

      <div>
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Canal de Servicio <span class="text-red-500">*</span>
        </label>
        <p-dropdown
          [options]="canalesServicio()"
          optionLabel="label"
          optionValue="value"
          [(ngModel)]="form().dscanalServicio"
          placeholder="Seleccione..."
          styleClass="w-full"
        />
      </div>

      <div>
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Forma de Entrega <span class="text-red-500">*</span>
        </label>
        <p-dropdown
          [options]="formasEntrega()"
          optionLabel="label"
          optionValue="value"
          [filter]="true"
          filterBy="label"
          [(ngModel)]="form().formaEntrega"
          placeholder="Seleccione..."
          styleClass="w-full"
          (onChange)="formaEntregaChange.emit()"
        />
      </div>

      @if (esTelefonico()) {
        <div class="sm:col-span-2">
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Tipo de Llamada <span class="text-red-500">*</span>
          </label>
          <p-dropdown
            [options]="tiposLlamada()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="form().tipoLlamada"
            placeholder="Seleccione..."
            styleClass="w-full"
          />
        </div>
      }

      <div>
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Municipio Origen <span class="text-red-500">*</span>
        </label>
        <div class="flex gap-1 min-w-0">
          <input
            pInputText
            [value]="municipioOrigenLabel()"
            readonly
            placeholder="Buscar municipio..."
            class="flex-1 min-w-0 cursor-pointer"
            (click)="abrirBusquedaMunicipioOrigen.emit()"
          />
          <button
            pButton
            icon="pi pi-search"
            pTooltip="Buscar municipio origen"
            class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
            (click)="abrirBusquedaMunicipioOrigen.emit()"
          ></button>
          @if (municipioOrigen()) {
            <button
              pButton
              icon="pi pi-times"
              pTooltip="Limpiar"
              class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
              (click)="limpiarMunicipioOrigen.emit()"
            ></button>
          }
        </div>
      </div>

      <div>
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Municipio Entrega <span class="text-red-500">*</span>
        </label>
        <div class="flex gap-1 min-w-0">
          <input
            pInputText
            [value]="municipioEntregaLabel()"
            readonly
            placeholder="Buscar municipio..."
            class="flex-1 min-w-0 cursor-pointer"
            (click)="abrirBusquedaMunicipioEntrega.emit()"
          />
          <button
            pButton
            icon="pi pi-search"
            pTooltip="Buscar municipio entrega"
            class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
            (click)="abrirBusquedaMunicipioEntrega.emit()"
          ></button>
          @if (municipioEntrega()) {
            <button
              pButton
              icon="pi pi-times"
              pTooltip="Limpiar"
              class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
              (click)="limpiarMunicipioEntrega.emit()"
            ></button>
          }
        </div>
      </div>

      <!-- Legacy: slFechaEntregaInicialTarea / dfFechaEntregaInicialTarea /
           slFechaEntregaFinalTarea / dfFechaEntregaFinalTarea / lbCantidadTarea /
           tbCantidadTarea (seguro="true"): solo existen para Crear Tarea, bajo la
           misma condición (recurso SEUS siCrearTarea) que el botón. -->
      @if (puedeCrearTarea()) {
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Fecha Entrega Inicial <span class="text-red-500">*</span>
          </label>
          <p-datepicker
            [ngModel]="fechaEntregaInicial()"
            [showIcon]="true"
            dateFormat="dd/mm/yy"
            placeholder="dd/mm/aaaa"
            appendTo="body"
            [showTransitionOptions]="'0ms'"
            [hideTransitionOptions]="'0ms'"
            styleClass="w-full"
            (ngModelChange)="fechaEntregaInicialChange.emit($event)"
          />
        </div>

        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Fecha Entrega Final <span class="text-red-500">*</span>
          </label>
          <p-datepicker
            [ngModel]="fechaEntregaFinal()"
            [showIcon]="true"
            dateFormat="dd/mm/yy"
            placeholder="dd/mm/aaaa"
            appendTo="body"
            [showTransitionOptions]="'0ms'"
            [hideTransitionOptions]="'0ms'"
            styleClass="w-full"
            (ngModelChange)="fechaEntregaFinalChange.emit($event)"
          />
        </div>

        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Cantidad <span class="text-red-500">*</span>
          </label>
          <p-inputNumber
            appDecimalComa
            [(ngModel)]="form().cantidadTarea"
            [locale]="localeNumerico"
            [min]="0.01"
            [minFractionDigits]="0"
            [maxFractionDigits]="maxDecimalesCantidad"
            [useGrouping]="false"
            [showButtons]="false"
            ariaDescribedBy="ayuda-cantidad-tarea"
            placeholder="0,00"
            styleClass="w-full"
          />
          <app-decimal-hint id="ayuda-cantidad-tarea" ejemplo="1,5" />
        </div>
      }

      @if (mostrarExpediente()) {
        <div class="sm:col-span-2">
          <div
            class="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg mb-3"
          >
            <p class="text-xs text-amber-700 dark:text-amber-300 font-medium">
              <i class="pi pi-info-circle mr-1"></i>
              La tarea seleccionada requiere información de expediente.
            </p>
          </div>
        </div>

        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Expediente <span class="text-red-500">*</span>
          </label>
          <input
            pInputText
            type="text"
            inputmode="numeric"
            autocomplete="off"
            [(ngModel)]="form().nmexpediente"
            (keypress)="onlyNumbersKeypress($event)"
            (change)="form().nmexpediente = sanitizeNumbersOnly(form().nmexpediente)"
            [maxlength]="maxDigitosExpediente"
            placeholder="Número de expediente"
            class="w-full"
          />
        </div>

        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Estado de Investigación <span class="text-red-500">*</span>
          </label>
          <p-dropdown
            [options]="estadosInvestigacion()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="form().dsestadoInvestigacion"
            placeholder="Seleccione..."
            styleClass="w-full"
          />
        </div>
      }
    </div>

    <div
      class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full mt-6 pt-4 border-t border-gray-200 dark:border-gray-700"
    >
      <button
        pButton
        label="Limpiar"
        icon="pi pi-eraser"
        class="p-button-outlined p-button-secondary min-h-[44px]"
        (click)="limpiarFormulario.emit()"
        [disabled]="loading()"
      ></button>
      <!-- Legacy: siCrearTarea (seguro="true") — recurso SEUS -->
      @if (puedeCrearTarea()) {
        <button
          pButton
          label="Crear Tarea"
          icon="pi pi-check"
          class="p-button-primary min-h-[44px]"
          (click)="crearTarea.emit()"
          [disabled]="loading()"
        ></button>
      }
      <button
        pButton
        label="Ingresar Cumplimiento"
        icon="pi pi-upload"
        class="p-button-outlined min-h-[44px]"
        (click)="ingresarCumplimiento.emit()"
        [disabled]="loading()"
      ></button>
    </div>
  `
})
export class FormularioCrearTareaSinOcComponent {
  readonly maxDigitosExpediente = MAX_DIGITOS_EXPEDIENTE;
  readonly localeNumerico = LOCALE_NUMERICO;
  readonly maxDecimalesCantidad = MAX_DECIMALES_CANTIDAD;
  sanitizeNumbersOnly = sanitizeNumbersOnly;
  onlyNumbersKeypress = onlyNumbersKeypress;

  form = input.required<Partial<CrearTareaRequest>>();
  loading = input<boolean>(false);
  puedeCrearTarea = input<boolean>(false);
  oficinas = input<Oficina[]>([]);

  formasEntrega = input<Array<{ label: string; value: string }>>([]);
  tiposLlamada = input<Array<{ label: string; value: string }>>([]);
  canalesServicio = input<Array<{ label: string; value: string }>>([]);
  estadosInvestigacion = input<Array<{ label: string; value: string }>>([]);

  aseguradoraLabel = input<string>('');
  nombreBeneficiarioDisplay = input<string>('');
  descripcionTarea = input<string>('');

  asesorLabel = input<string>('');
  asesorBloqueado = input<boolean>(false);
  esTelefonico = input<boolean>(false);
  mostrarExpediente = input<boolean>(false);

  nmconsecBeneficiarioLocal = input<number>(0);
  municipioOrigen = input<Municipio | null>(null);
  municipioEntrega = input<Municipio | null>(null);
  municipioOrigenLabel = input<string>('');
  municipioEntregaLabel = input<string>('');

  fechaEntregaInicial = input<Date | null>(null);
  fechaEntregaFinal = input<Date | null>(null);

  oficinaChange = output<{ value: number }>();
  formaEntregaChange = output<void>();

  abrirBusquedaAseguradora = output<void>();
  limpiarAseguradora = output<void>();
  abrirBusquedaBeneficiario = output<void>();
  limpiarBeneficiario = output<void>();
  abrirBusquedaTarea = output<void>();
  limpiarTarea = output<void>();
  abrirBusquedaCentroTrabajo = output<void>();
  limpiarCentroTrabajo = output<void>();
  abrirBusquedaAsesor = output<void>();
  limpiarAsesor = output<void>();
  abrirBusquedaProducto = output<void>();
  limpiarProducto = output<void>();
  abrirBusquedaMunicipioOrigen = output<void>();
  limpiarMunicipioOrigen = output<void>();
  abrirBusquedaMunicipioEntrega = output<void>();
  limpiarMunicipioEntrega = output<void>();

  fechaEntregaInicialChange = output<Date | null>();
  fechaEntregaFinalChange = output<Date | null>();

  limpiarFormulario = output<void>();
  crearTarea = output<void>();
  ingresarCumplimiento = output<void>();
}
